import type { CpuStatsPlayer } from '@xox/shared'
import { ArrowLeft, House, Swords } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useCreateRoom } from '../../hooks/useCreateRoom'
import { useFetch } from '../../hooks/useFetch'
import { Confetti } from '../../ice/art/Confetti'
import { BOSS_THEME, type BossTheme } from '../../lib/bossTheme'
import { plural } from '../../lib/format'
import type { SnapshotOf } from '../../store/duel'
import { useSession } from '../../store/session'
import { Avatar } from '../Avatar'
import { BossHero } from '../BossHero'
import { loadHumanFirst } from '../BossStartPicker'
import { BottomNav, bossTab, joinTab, menuTab } from '../BottomNav'
import { CpuRecordCard } from '../CpuRecordCard'
import { StatChips } from '../StatChips'
import { Button, Card, LinkButton } from '../ui'
import { DuelSheets } from './DuelSheets'
import type { DuelPageProps, DuelSettingsBase } from './defs'

type Sheet = 'join' | 'menu' | null

/** A bot's Hall of Attempts: who tried, who held a draw, who ran away. */
export function BossWall<Snap extends SnapshotOf<S>, S extends DuelSettingsBase>({
  def,
}: DuelPageProps<Snap, S>) {
  const me = useSession((s) => s.me)
  const navigate = useNavigate()
  const { data, error } = useFetch(def.api.cpuStats)
  const { create, creating, error: createError } = useCreateRoom(def)
  const [sheet, setSheet] = useState<Sheet>(null)
  const closeSheet = () => setSheet(null)
  const theme = BOSS_THEME[def.id]
  const challenge = () => create('cpu', { humanFirst: loadHumanFirst(def.id) })

  const players = data?.players ?? []
  const mine = players.find((p) => p.playerId === me?.playerId)
  const maxLongest = Math.max(1, ...players.map((p) => p.longestGame))
  const longest = players.reduce<CpuStatsPlayer | null>(
    (best, p) => (best === null || p.longestGame > best.longestGame ? p : best),
    null,
  )
  const drawClub = players.filter((p) => p.draws > 0)
  const slayer = players.find((p) => p.wins > 0)

  const challengeButton = (
    <Button
      variant="arcade"
      tone={theme.arcade}
      className="w-full"
      onClick={challenge}
      disabled={creating}
    >
      <Swords className="size-5" aria-hidden /> Challenge
    </Button>
  )

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <LinkButton to={def.home} className="self-start px-2">
        <ArrowLeft className="size-4" aria-hidden /> Back
      </LinkButton>

      <BossHero
        game={def.id}
        bot={def.bot}
        eyebrow="Hall of attempts"
        title={def.boss.title}
        subtitle={`Everyone who challenged ${def.bot.nickname}`}
        heading="h1"
      >
        <StatChips
          className="mt-4"
          items={[
            { label: 'Attempts', value: data?.totals.games },
            { label: 'Wins', value: data?.totals.wins },
            { label: 'Draws', value: data?.totals.draws },
            { label: 'Gave up', value: data?.totals.quits },
          ]}
        />
        {data?.rules.edgeOpening && (
          <p className={`mt-3 text-center text-xs ${theme.sub}`}>
            House rule on: no edge openings when you start.
          </p>
        )}
      </BossHero>

      {error && <p className="text-sm text-rose-600">{error}</p>}

      {mine ? (
        <CpuRecordCard
          bot={def.bot}
          load={def.api.cpuStatsSoon}
          playerId={me?.playerId ?? null}
          stats={data}
        />
      ) : (
        data && (
          <Card className="flex flex-col gap-3">
            <div>
              <h2 className="font-semibold">Not on the wall yet</h2>
              <p className="text-sm text-slate-500">
                {players.length === 0
                  ? 'Nobody has dared yet. Be the first to lose.'
                  : 'One game puts your name up here. Losing counts.'}
              </p>
            </div>
            {challengeButton}
            {createError && <p className="text-sm text-rose-600">{createError}</p>}
          </Card>
        )
      )}

      {players.length > 0 && (
        <section aria-label="Highlights" className="grid gap-3 sm:grid-cols-2">
          {slayer && (
            <Card className="relative overflow-hidden sm:col-span-2">
              <Confetti />
              <Spotlight
                theme={theme}
                label="Giant slayer"
                player={slayer}
                detail={`beat ${def.bot.nickname} ${plural(slayer.wins, 'time')}`}
              />
            </Card>
          )}
          {longest && (
            <Card>
              <Spotlight
                theme={theme}
                label="Longest fight"
                player={longest}
                detail={plural(longest.longestGame, 'move')}
              />
            </Card>
          )}
          <Card>
            <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${theme.accent}`}>
              Draw club
            </div>
            {drawClub.length === 0 ? (
              <p className="mt-1 text-sm text-slate-500">
                Empty. A draw is the best anyone can do.
              </p>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <div className="flex -space-x-2">
                  {drawClub.slice(0, 3).map((p) => (
                    <Avatar
                      key={p.playerId}
                      id={p.playerId}
                      avatar={p.avatar}
                      color={p.color}
                      size="sm"
                      className="ring-2 ring-white dark:ring-slate-900"
                    />
                  ))}
                </div>
                <div className="text-sm font-semibold">
                  {plural(drawClub.length, 'member')}
                  <span className="ml-1 text-xs font-medium text-slate-500">held a draw</span>
                </div>
              </div>
            )}
          </Card>
        </section>
      )}

      {players.length > 0 && (
        <Card className="divide-y divide-slate-100 p-0 dark:divide-slate-800">
          {players.map((p, i) => (
            <RecordRow
              key={p.playerId}
              theme={theme}
              rank={i + 1}
              player={p}
              isMe={p.playerId === me?.playerId}
              share={p.longestGame / maxLongest}
            />
          ))}
        </Card>
      )}

      <BottomNav
        tabs={[
          {
            key: 'home',
            label: 'Home',
            icon: <House className="size-5" aria-hidden />,
            onSelect: () => navigate(def.home),
          },
          bossTab(def.id, def.bot, 'Challenge', () => void challenge(), creating),
          joinTab(sheet, setSheet),
          menuTab(sheet, setSheet),
        ]}
      />
      <DuelSheets def={def} sheet={sheet} onClose={closeSheet} joinId="wall-join-code" />
    </div>
  )
}

function Spotlight({
  theme,
  label,
  player,
  detail,
}: {
  theme: BossTheme
  label: string
  player: CpuStatsPlayer
  detail: string
}) {
  return (
    <div className="relative">
      <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${theme.accent}`}>
        {label}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <Avatar id={player.playerId} avatar={player.avatar} color={player.color} size="sm" />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{player.nickname}</div>
          <div className="text-xs text-slate-500">{detail}</div>
        </div>
      </div>
    </div>
  )
}

const RANK_RING = [
  'bg-[var(--ice-brass)] text-slate-900',
  'bg-slate-300 text-slate-900 dark:bg-slate-400',
  'bg-[var(--ice-wood)] text-white',
] as const

function RecordRow({
  theme,
  rank,
  player,
  isMe,
  share,
}: {
  theme: BossTheme
  rank: number
  player: CpuStatsPlayer
  isMe: boolean
  share: number
}) {
  const ring =
    RANK_RING[rank - 1] ?? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 first:rounded-t-2xl last:rounded-b-2xl ${isMe ? 'bg-sky-50 dark:bg-sky-950/40' : ''}`}
    >
      <span
        className={`flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-black tabular-nums ${ring}`}
      >
        {rank}
      </span>
      <Avatar id={player.playerId} avatar={player.avatar} color={player.color} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={`truncate text-sm ${isMe ? 'font-bold' : 'font-semibold'}`}>
            {player.nickname}
          </span>
          {isMe && <span className="text-xs text-sky-600">(you)</span>}
          {player.draws > 0 && (
            <span
              className={`rounded-full px-1.5 text-[10px] font-bold uppercase ${theme.accentBg}`}
            >
              draw club
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500 tabular-nums">
          <span>{plural(player.games, 'try', 'tries')}</span>
          {player.wins > 0 && (
            <span className="font-semibold text-emerald-600">{player.wins} W</span>
          )}
          <span>{player.draws} D</span>
          <span>{player.losses} L</span>
          {player.quits > 0 && <span>{player.quits} quit</span>}
        </div>
      </div>
      <div className="w-20 shrink-0 text-right">
        <div className="text-sm font-black tabular-nums">{player.longestGame}</div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">moves</div>
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`h-full rounded-full ${theme.bar}`}
            style={{ width: `${Math.round(share * 100)}%` }}
          />
        </div>
      </div>
    </div>
  )
}
